import { NumberFormatter } from '@core/format';
import dayjs from 'dayjs';
import { Locales } from '../strings/Locales.js';
var rePeriodPresetUnit = /^(second|minute|hour|day|week|month|year)s?$/;
var normalizeUnit = function normalizeUnit(unit) {
    var match = rePeriodPresetUnit.exec(unit);
    if (!match) {
        throw new RangeError("Invalid unit: ".concat(unit));
    }
    return match[1];
};
/**
 * (Internal) utility method that builds a label. It first looks if there's an "override" key,
 * otherwise it builds a generic label. Examples of such overrides are 'current.day' -> "Today",
 * and 'last.day' -> "Yesterday" (in English). The generic representation will be "Last (unit)".
 *
 * @param prefix The prefix of the label key, indicating where on the timeline the range falls.
 * @param length The length of the range, in unit.
 * @param presetUnit The unit of the range.
 * @returns The label representing the range.
 */ var formatLabel = function formatLabel(prefix, length, presetUnit) {
    var period;
    var unit = normalizeUnit(presetUnit);
    if (length === 1) {
        var _ref;
        var _parts_find;
        var key = "PeriodPreset.".concat(prefix, ".").concat(unit);
        // Locales has no API to check if there is a key present, so we can only format and check if the value comes back as the key.
        var label = Locales.format(key);
        if (label !== key) {
            return label;
        }
        var parts = NumberFormatter.unitFull({
            unit: unit
        }).formatToParts(1);
        period = (_ref = (_parts_find = parts.find(function(part) {
            return part.type === 'unit';
        })) === null || _parts_find === void 0 ? void 0 : _parts_find.value) !== null && _ref !== void 0 ? _ref : presetUnit;
    } else {
        period = NumberFormatter.unitFull({
            unit: unit
        }).format(length);
    }
    return Locales.format("PeriodPreset.".concat(prefix, ".generic"), {
        smart_count: length,
        period: period
    });
};
var rangeFrom = function rangeFrom(startOffset, endOffset, unit) {
    var reference = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : dayjs();
    var start = reference.add(startOffset, unit).startOf(unit).toISOString();
    // If the endOffset is less than startOffset, default to an empty range (start === end).
    var end = endOffset < startOffset ? start : reference.add(endOffset, unit).endOf(unit).toISOString();
    return [
        start,
        end
    ];
};
var last = function last(length, unit) {
    var reference = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : dayjs();
    if (length < 1 || !Number.isInteger(length)) {
        throw new Error("PeriodPreset.last requires an integer length of at least 1, got: ".concat(length));
    }
    return Object.freeze({
        label: formatLabel('last', length, unit),
        range: rangeFrom(-length, -1, unit, reference)
    });
};
var current = function current(unit) {
    var reference = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : dayjs();
    return Object.freeze({
        label: formatLabel('current', 1, unit),
        range: rangeFrom(0, 0, unit, reference)
    });
};
var next = function next(length, unit) {
    var reference = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : dayjs();
    if (length < 1 || !Number.isInteger(length)) {
        throw new Error("PeriodPreset.next requires an integer length of at least 1, got: ".concat(length));
    }
    return Object.freeze({
        label: formatLabel('next', length, unit),
        range: rangeFrom(1, length, unit, reference)
    });
};
// This method ensures the presets adhere to the expected type (record of DateRangePeriodPreset), while still allowing to infer the keys.
var define = function define(presets) {
    return Object.freeze(presets);
};
/**
 * Utilities to define Date/Time picker presets.
 */ export var PeriodPreset = {
    /**
     * Determine a range from start and end offset, in the specified `unit`.
     * Note that offset `0` equals "current", e.g. for minute `0` is the current minute.
     * Positive offsets move forward in time (compared to `reference`) and conversely negative offsets move backward in time.
     *
     * @param startOffset The offset of the start, in `unit` and relative to `reference`.
     * @param endOffset The offset of the end, in `unit` and relative to `reference`.
     * @param unit The unit the offsets are in.
     * @param reference Optional reference timestamp to base relative calculations from, defaults to "now".
     * @returns The range as an array in the form `[start, end]`, where both are `Date` values.
     */ rangeFrom: rangeFrom,
    /**
     * Generate a preset, including label, for the last "length units", e.g. the "last 24 hours" or "last year".
     * The range will end at the last full unit, e.g. last year will start at January 1st, and end at December 31st.
     * Note that this function always moves into the past, and `length` must be at least 1.
     * The unit `'week'` is locale dependant, it may select a different start-of-week day based on the selected
     * locale (provided culture metadata is loaded).
     * Change dayjs's default locale, or pass a `reference` in a different locale, to get this behavior.
     *
     * @param length The (positive >= 1) integer length of the range, which determines how far in the past the range starts.
     * @param unit The unit the range is in.
     * @param reference Optional reference timestamp to base relative calculations from, defaults to "now".
     * @returns A `DateRangePeriodPreset` object with a label and `[start, end]` `Date` range.
     */ last: last,
    /**
     * Generate a preset, including label, for the range of the current unit, e.g. the "this week" or "today".
     * The range will likely end in the future, e.g. this year will end at December 31st of the current year.
     * The unit `'week'` is locale dependant, it may select a different start-of-week day based on the selected
     * locale (provided culture metadata is loaded).
     * Change dayjs's default locale, or pass a `reference` in a different locale, to get this behavior.
     *
     * @param length The (positive >= 1) integer length of the range, which determines how far in the past the range starts.
     * @param unit The unit the range is in.
     * @param reference Optional reference timestamp to base relative calculations from, defaults to "now".
     * @returns A `DateRangePeriodPreset` object with a label and `[start, end]` `Date` range.
     */ current: current,
    /**
     * Generate a preset, including label, for the next "length units", e.g. the "tomorrow" or "next month".
     * The range will start at the next full unit, e.g. next year will start at January 1st, and end at December 31st.
     * Note that this function always moves into the future, and `length` must be at least 1.
     * The unit `'week'` is locale dependant, it may select a different start-of-week day based on the selected
     * locale (provided culture metadata is loaded).
     * Change dayjs's default locale, or pass a `reference` in a different locale, to get this behavior.
     *
     * @param length The (positive >= 1) integer length of the range, which determines how far in the past the range starts.
     * @param unit The unit the range is in.
     * @param reference Optional reference timestamp to base relative calculations from, defaults to "now".
     * @returns A `DateRangePeriodPreset` object with a label and `[start, end]` `Date` range.
     */ next: next,
    /**
     * Shortcut for last(1, 'day', reference), resolving to a "yesterday" range.
     * @param reference Optional reference timestamp to base relative calculations from, defaults to "now".
     * @returns A `DateRangePeriodPreset` object representing yesterday.
     */ yesterday: last.bind(null, 1, 'day'),
    /**
     * Shortcut for current('day', reference), resolving to a "today" range.
     * @param reference Optional reference timestamp to base relative calculations from, defaults to "now".
     * @returns A `DateRangePeriodPreset` object representing today.
     */ today: current.bind(null, 'day'),
    /**
     * Shortcut for next(1, 'day', reference), resolving to a "tomorrow" range.
     * @param reference Optional reference timestamp to base relative calculations from, defaults to "now".
     * @returns A `DateRangePeriodPreset` object representing tomorrow.
     */ tomorrow: next.bind(null, 1, 'day'),
    /**
     * Utility method that allows defining an object of presets in a type-safe manner, while still inferring the keys automatically.
     *
     * @param presets The object that defines the presets; will be frozen using `Object.freeze`.
     * @returns The input `presets`, but frozen.
     */ define: define
};

//# sourceMappingURL=PeriodPreset.js.map